// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWizard, deriveProfileName } from '../WizardContext';
import { useToast, InputLabel, Dropdown, TextInput, Button, Checkbox, AppIcon } from '@loa/ui-components';
import { policyApi } from '../../../api/policyApi';
import { FileUploadField } from '../../providerAccess/shared/FileUploadField';
import { brokerageConfigApi, type BrokerageProvider } from '../../../api/brokerageConfigApi';
import { advisorApi } from '../../../api/advisorApi';
import { usePolicyTypeOptions } from '../../../hooks/usePolicyTypeOptions';
import type { PolicyCreateDTO, SignatoryInput } from '../../../types/policy';
import * as yup from 'yup';

const policyFormSchema = yup.object({
  providerId: yup.string().required('Provider is required'),
});

// Figma-spec policy checkboxes: 24x24, #2D0A6C rounded border when empty, fully
// filled #2D0A6C with a white check when ticked. Applied as `!`-important arbitrary
// variants on the shared Checkbox's internal markup so they out-rank the design-system
// base rules without touching @loa/ui-components (same approach as the fact-find
// GateControl radios — keeps styling in Tailwind, no app-level CSS file).
const POLICY_CHECKBOX_CLASS =
  '[&_.fin-checkbox__control]:w-[24px]! [&_.fin-checkbox__control]:h-[24px]! ' +
  '[&_.fin-checkbox__control]:border-2! [&_.fin-checkbox__control]:border-solid! ' +
  '[&_.fin-checkbox__control]:border-purple-primary! [&_.fin-checkbox__control]:rounded-lg! ' +
  '[&_.fin-checkbox__input:checked+.fin-checkbox__control]:bg-purple-primary! ' +
  '[&_.fin-checkbox__input:checked+.fin-checkbox__control]:border-purple-primary!';

type View = 'choice' | 'form' | 'list';

interface SavedPolicy {
  id: string;
  providerId: string;
  providerName: string;
  category?: string;
  policyTypeName?: string;
  policyNumber?: string;
  ownershipType: string;
  clientNames: string[];
  inAgency: boolean;
  loaInPlace: boolean;
  clientRoles: ClientRoleMap;
}

type ClientRoleMap = Record<string, { signatory: boolean; owner: boolean }>;
type CreatedClientIdMap = Record<string, string>;
type ClientWithTempId = { tempId: string };

function hasAnySignatorySelected(clientRoles: ClientRoleMap): boolean {
  return Object.values(clientRoles).some(r => r?.signatory);
}

function resolveJointRelationship(
  roles: { signatory: boolean; owner: boolean } | undefined,
  hasAnySignatory: boolean,
): SignatoryInput['relationshipType'] | null {
  if (!roles) return null;
  if (roles.owner && roles.signatory) return 'OWNER_AND_SIGNATORY';
  // No signatory selected anywhere → the owner stands in as the signatory.
  if (roles.owner) return hasAnySignatory ? 'OWNER' : 'OWNER_AND_SIGNATORY';
  if (roles.signatory) return 'SIGNATORY';
  return null;
}

function buildJointSignatories(
  clients: ReadonlyArray<ClientWithTempId>,
  createdClientIds: CreatedClientIdMap,
  clientRoles: ClientRoleMap,
): SignatoryInput[] {
  const hasAnySignatory = hasAnySignatorySelected(clientRoles);
  const signatories: SignatoryInput[] = [];
  for (const client of clients) {
    const realId = createdClientIds[client.tempId];
    if (!realId) continue;
    const relationshipType = resolveJointRelationship(clientRoles[client.tempId], hasAnySignatory);
    if (relationshipType) signatories.push({ clientId: realId, relationshipType });
  }
  return signatories;
}

function buildSoloSignatoryIds(createdClientIds: CreatedClientIdMap): string[] | undefined {
  const ids = Object.values(createdClientIds);
  return ids.length === 0 ? undefined : ids;
}

function buildSoloClientRoles(clients: ReadonlyArray<ClientWithTempId>): ClientRoleMap {
  const roles: ClientRoleMap = {};
  for (const c of clients) roles[c.tempId] = { signatory: true, owner: true };
  return roles;
}

// Only consumed by the unreachable relationship-fetch fallback in
// resolveExpandedClientRoles (see the v8-ignore block there).
/* v8 ignore start */
function buildEmptyClientRoles(clients: ReadonlyArray<ClientWithTempId>): ClientRoleMap {
  const roles: ClientRoleMap = {};
  for (const c of clients) roles[c.tempId] = { signatory: false, owner: false };
  return roles;
}
/* v8 ignore stop */

async function uploadPolicyDocuments(policyId: string, files: File[]): Promise<void> {
  for (const file of files) {
    await policyApi.uploadPolicyDocument(policyId, file);
  }
}



export function AddPoliciesStep() {
  const navigate = useNavigate();
  const { state } = useWizard();
  const { showToast } = useToast();

  const [view, setView] = useState<View>('choice');
  const [savedPolicies, setSavedPolicies] = useState<SavedPolicy[]>([]);
  const [editingPolicyId, setEditingPolicyId] = useState<string | null>(null);
  const [expandedPolicyId, setExpandedPolicyId] = useState<string | null>(null);
  const [showInlineAdd, setShowInlineAdd] = useState(false);

  // Edit form state
  const [providerId, setProviderId] = useState('');
  const [category, setCategory] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [inAgency, setInAgency] = useState(false);
  const [loaInPlace, setLoaInPlace] = useState(false);
  const [policyDocs, setPolicyDocs] = useState<File[]>([]);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Client role selections for JOINT policies: clientTempId -> { signatory, owner }
  const [clientRoles, setClientRoles] = useState<Record<string, { signatory: boolean; owner: boolean }>>({});

  // Add form state (separate from edit to avoid conflicts)
  const [addProviderId, setAddProviderId] = useState('');
  const [addCategory, setAddCategory] = useState('');
  const [addPolicyNumber, setAddPolicyNumber] = useState('');
  const [addInAgency, setAddInAgency] = useState(false);
  const [addLoaInPlace, setAddLoaInPlace] = useState(false);
  const [addPolicyDocs, setAddPolicyDocs] = useState<File[]>([]);
  const [addFormErrors, setAddFormErrors] = useState<Record<string, string>>({});
  const [addClientRoles, setAddClientRoles] = useState<Record<string, { signatory: boolean; owner: boolean }>>({});

  // Config data
  const [providers, setProviders] = useState<BrokerageProvider[]>([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [brokerageId, setBrokerageId] = useState<string>('');

  // Dynamic policy type options per provider
  const { categoryOptions: formCategoryOptions } = usePolicyTypeOptions(brokerageId, providerId);
  const { categoryOptions: addCategoryOptions } = usePolicyTypeOptions(brokerageId, addProviderId);

  const profileId = state.createdProfileId;
  const isJoint = state.profileData.profileType === 'JOINT';

  const isFormComplete = useMemo(
    () => policyFormSchema.isValidSync({ providerId }),
    [providerId]
  );

  const isAddFormComplete = useMemo(
    () => policyFormSchema.isValidSync({ providerId: addProviderId }),
    [addProviderId]
  );

  const jointOwnerOk = useMemo(
    () => Object.values(clientRoles).some(r => r?.owner),
    [clientRoles]
  );

  const addJointOwnerOk = useMemo(
    () => Object.values(addClientRoles).some(r => r?.owner),
    [addClientRoles]
  );

  // Load brokerage configuration
  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoadingConfig(true);
        const advisorProfile = await advisorApi.getProfile();
        const brokId = advisorProfile.brokerageId.toString();
        setBrokerageId(brokId);
        const providersResponse = await brokerageConfigApi.getEnabledProviders(brokId);
        setProviders(providersResponse.content);
      } catch (error) {
        console.error('Failed to load brokerage configuration:', error);
      } finally {
        setLoadingConfig(false);
      }
    };
    loadConfig();
  }, []);

  // Initialize client roles when entering form view for JOINT
  const initClientRoles = () => {
    const roles: Record<string, { signatory: boolean; owner: boolean }> = {};
    state.clients.forEach(c => {
      roles[c.tempId] = { signatory: false, owner: false };
    });
    setClientRoles(roles);
  };

  const toggleClientRole = (tempId: string, role: 'signatory' | 'owner') => {
    setClientRoles(prev => ({
      ...prev,
      [tempId]: { ...prev[tempId], [role]: !prev[tempId]?.[role] },
    }));
  };

  const initAddClientRoles = () => {
    const roles: Record<string, { signatory: boolean; owner: boolean }> = {};
    state.clients.forEach(c => {
      roles[c.tempId] = { signatory: false, owner: false };
    });
    setAddClientRoles(roles);
  };

  const toggleAddClientRole = (tempId: string, role: 'signatory' | 'owner') => {
    setAddClientRoles(prev => ({
      ...prev,
      [tempId]: { ...prev[tempId], [role]: !prev[tempId]?.[role] },
    }));
  };


  const resolveExpandedClientRoles = async (policy: SavedPolicy) => {
    if (policy.clientRoles && Object.keys(policy.clientRoles).length > 0) {
      setClientRoles(policy.clientRoles);
      return;
    }
    // Fallback for policies that arrive without in-memory roles (e.g. a future
    // "load existing policies / resume draft" path). Unreachable in the current
    // wizard — policies created in-session always carry their clientRoles, so
    // the early return above always wins.
    /* v8 ignore start */
    setClientRoles(buildEmptyClientRoles(state.clients));
    try {
      const relationships = await policyApi.getRelationships(policy.id);
      const realToTemp: Record<string, string> = {};
      for (const [tempId, realId] of Object.entries(state.createdClientIds)) {
        realToTemp[realId] = tempId;
      }
      const roles = buildEmptyClientRoles(state.clients);
      for (const rel of relationships) {
        const tempId = realToTemp[rel.clientId];
        if (!tempId) continue;
        const t = rel.relationshipType as string;
        roles[tempId] = {
          owner: t === 'OWNER' || t === 'OWNER_AND_SIGNATORY',
          signatory: t === 'SIGNATORY' || t === 'OWNER_AND_SIGNATORY',
        };
      }
      setClientRoles(roles);
    } catch (error) {
      console.error('Failed to resolve policy relationships:', error);
    }
    /* v8 ignore stop */
  };

  const handleToggleExpand = (policy: SavedPolicy) => {
    if (expandedPolicyId === policy.id) {
      // Collapse
      setExpandedPolicyId(null);
      setEditingPolicyId(null);
      setProviderId('');
      setCategory('');
      setPolicyNumber('');
      setInAgency(false);
      setLoaInPlace(false);
      setClientRoles({});
      setPolicyDocs([]);
      setFormErrors({});
    } else {
      // Expand and pre-fill
      setExpandedPolicyId(policy.id);
      setEditingPolicyId(policy.id);
      setProviderId(policy.providerId);
      setCategory(policy.category || '');
      setPolicyNumber(policy.policyNumber || '');
      setInAgency(policy.inAgency);
      setLoaInPlace(policy.loaInPlace);
      setPolicyDocs([]);
      setFormErrors({});
      void resolveExpandedClientRoles(policy);
    }
  };

  const validateSaveForm = (): Record<string, string> | null => {
    const errors: Record<string, string> = {};
    if (!providerId) errors.providerId = 'Provider is required';
    if (!category) errors.category = 'Policy type is required';
    if (isJoint) {
      const hasOwner = Object.values(clientRoles).some(r => r.owner);
      if (!hasOwner) errors.clientRoles = 'At least one client must be an owner';
    }
    return Object.keys(errors).length === 0 ? null : errors;
  };

  const updateExistingPolicy = async (
    profileIdSafe: string,
    editingId: string,
    provider: BrokerageProvider | undefined,
    categoryLabel: string | undefined,
  ) => {
    const updated = await policyApi.updatePolicy(profileIdSafe, editingId, {
      providerId,
      category: category || undefined,
      policyNumber: policyNumber.trim() || undefined,
      inAgency,
      loaInPlace,
    });

    if (isJoint) {
      await syncJointRelationships(editingId);
    }

    // Upload any newly-staged documents for this policy (same as the add
    // flow: staged via FileUploadField, uploaded on Save).
    if (policyDocs.length > 0) {
      await uploadPolicyDocuments(editingId, policyDocs);
    }

    setSavedPolicies(prev => prev.map(p =>
      p.id === editingId
        ? { ...p, providerId, providerName: provider?.provider.name || p.providerName, category: category || undefined, policyTypeName: categoryLabel, policyNumber: updated.policyNumber, inAgency, loaInPlace, clientRoles: isJoint ? clientRoles : p.clientRoles }
        : p,
    ));
    setEditingPolicyId(null);
    setExpandedPolicyId(null);
  };

  const syncJointRelationships = async (policyId: string) => {
    const existing = await policyApi.getRelationships(policyId);
    // The relationships DTO carries both `clientProfileId` and `clientId`
    // (same value); key off whichever is present at runtime so the lookup
    // never misses and we don't try to re-create an existing relationship.
    const clientKey = (r: typeof existing[number]) =>
      ((r as { clientProfileId?: string }).clientProfileId ?? r.clientId);
    const existingByClient = new Map(existing.map(r => [clientKey(r), r]));

    const hasAnySignatory = hasAnySignatorySelected(clientRoles);
    for (const client of state.clients) {
      const realId = state.createdClientIds[client.tempId];
      if (!realId) continue;
      const desired = resolveJointRelationship(clientRoles[client.tempId], hasAnySignatory);
      const current = existingByClient.get(realId);

      try {
        if (desired && !current) {
          await policyApi.createRelationship(policyId, {
            clientProfileId: realId,
            relationshipType: desired,
          });
        } else if (desired && current && current.relationshipType !== desired) {
          await policyApi.updateRelationship(policyId, current.id, {
            relationshipType: desired,
          });
        } else if (!desired && current) {
          await policyApi.deleteRelationship(policyId, current.id);
        }
      } catch (error) {
        // "already exists" means the desired relationship is already in
        // place — benign. Any other per-client failure must not abort the
        // whole save (policy fields/flags still persist).
        console.error('Relationship sync failed for client', realId, error);
      }
    }
  };

  const createNewPolicy = async (
    profileIdSafe: string,
    provider: BrokerageProvider | undefined,
    categoryLabel: string | undefined,
    clientNames: string[],
  ) => {
    const ownershipType = isJoint ? 'JOINT' : 'SOLO';
    const { signatoryClientIds, signatories } = isJoint
      ? { signatoryClientIds: undefined, signatories: buildJointSignatories(state.clients, state.createdClientIds, clientRoles) }
      : { signatoryClientIds: buildSoloSignatoryIds(state.createdClientIds), signatories: undefined };
    const createData: PolicyCreateDTO = {
      providerId,
      category,
      policyNumber: policyNumber.trim() || undefined,
      status: 'active',
      ownershipType,
      signatoryClientIds,
      signatories: signatories && signatories.length > 0 ? signatories : undefined,
      inAgency,
      loaInPlace,
    };
    const created = await policyApi.createPolicy(profileIdSafe, createData);
    await uploadPolicyDocuments(created.id, policyDocs);
    setSavedPolicies(prev => [...prev, {
      id: created.id,
      providerId,
      providerName: provider?.provider.name || 'Unknown',
      category: category || undefined,
      policyTypeName: categoryLabel,
      policyNumber: created.policyNumber,
      ownershipType: created.ownershipType,
      clientNames,
      inAgency,
      loaInPlace,
      clientRoles: isJoint ? clientRoles : buildSoloClientRoles(state.clients),
    }]);
  };

  const resetSaveForm = () => {
    setProviderId('');
    setCategory('');
    setPolicyNumber('');
    setInAgency(false);
    setLoaInPlace(false);
    setPolicyDocs([]);
    setClientRoles({});
    setFormErrors({});
    setShowInlineAdd(false);
    setView('list');
  };

  const handleSavePolicy = async () => {
    if (!profileId) return;
    const errors = validateSaveForm();
    if (errors) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setSaving(true);
    try {
      const provider = providers.find(p => p.providerId === providerId);
      const categoryLabel = formCategoryOptions.find(c => c.value === category)?.text;
      const clientNames = state.clients.map(c => `${c.firstName} ${c.lastName}`);
      if (editingPolicyId) await updateExistingPolicy(profileId, editingPolicyId, provider, categoryLabel);
      else await createNewPolicy(profileId, provider, categoryLabel, clientNames);
      resetSaveForm();
    } catch (error) {
      console.error('Failed to save policy:', error);
      setFormErrors({ general: 'Failed to save policy. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAddPolicy = async () => {
    if (!profileId) return;

    const errors: Record<string, string> = {};
    if (!addProviderId) errors.providerId = 'Provider is required';
    if (!addCategory) errors.category = 'Category is required';

    if (isJoint) {
      const hasOwner = Object.values(addClientRoles).some(r => r.owner);
      if (!hasOwner) errors.clientRoles = 'At least one client must be an owner';
    }

    setAddFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      const provider = providers.find(p => p.providerId === addProviderId);
      const categoryLabel = addCategoryOptions.find(c => c.value === addCategory)?.text;
      const clientNames = state.clients.map(c => `${c.firstName} ${c.lastName}`);

      const ownershipType = isJoint ? 'JOINT' : 'SOLO';

      let signatoryClientIds: string[] | undefined;
      let signatories: SignatoryInput[] | undefined;

      if (isJoint) {
        signatories = [];
        const hasAnySignatory = Object.values(addClientRoles).some(r => r.signatory);
        for (const client of state.clients) {
          const realId = state.createdClientIds[client.tempId];
          if (!realId) continue;
          const roles = addClientRoles[client.tempId];
          if (!roles) continue;

          if (roles.owner && roles.signatory) {
            signatories.push({ clientId: realId, relationshipType: 'OWNER_AND_SIGNATORY' });
          } else if (roles.owner) {
            // No signatory selected anywhere → the owner stands in as the signatory.
            signatories.push({
              clientId: realId,
              relationshipType: hasAnySignatory ? 'OWNER' : 'OWNER_AND_SIGNATORY',
            });
          } else if (roles.signatory) {
            signatories.push({ clientId: realId, relationshipType: 'SIGNATORY' });
          }
        }
      } else {
        signatoryClientIds = Object.values(state.createdClientIds);
        if (signatoryClientIds.length === 0) signatoryClientIds = undefined;
      }

      const createData: PolicyCreateDTO = {
        providerId: addProviderId,
        category: addCategory,
        policyNumber: addPolicyNumber.trim() || undefined,
        status: 'active',
        ownershipType,
        signatoryClientIds,
        signatories: signatories && signatories.length > 0 ? signatories : undefined,
        inAgency: addInAgency,
        loaInPlace: addLoaInPlace,
      };

      const created = await policyApi.createPolicy(profileId, createData);
      await uploadPolicyDocuments(created.id, addPolicyDocs);

      setSavedPolicies(prev => [...prev, {
        id: created.id,
        providerId: addProviderId,
        providerName: provider?.provider.name || 'Unknown',
        category: addCategory || undefined,
        policyTypeName: categoryLabel,
        policyNumber: created.policyNumber,
        ownershipType: created.ownershipType,
        clientNames,
        inAgency: addInAgency,
        loaInPlace: addLoaInPlace,
        clientRoles: isJoint ? addClientRoles : buildSoloClientRoles(state.clients),
      }]);

      // Reset add form
      setAddProviderId('');
      setAddCategory('');
      setAddPolicyNumber('');
      setAddInAgency(false);
      setAddLoaInPlace(false);
      setAddPolicyDocs([]);
      setAddClientRoles({});
      setAddFormErrors({});
      setShowInlineAdd(false);
      setView('list');
    } catch (error) {
      console.error('Failed to save policy:', error);
      setAddFormErrors({ general: 'Failed to save policy. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePolicy = async (policyId: string) => {
    if (!profileId) return;
    setRemoving(true);
    try {
      await policyApi.deletePolicy(profileId, policyId, 'Removed during setup');
      setSavedPolicies(prev => prev.filter(p => p.id !== policyId));
      setExpandedPolicyId(null);
      setEditingPolicyId(null);
      setProviderId('');
      setCategory('');
      setPolicyNumber('');
      setInAgency(false);
      setLoaInPlace(false);
      setClientRoles({});
      setFormErrors({});
    } catch (error) {
      console.error('Failed to remove policy:', error);
    } finally {
      setRemoving(false);
    }
  };

  const handleDone = () => {
    if (profileId) {
      const profileName = state.profileData.profileName || deriveProfileName(state.profileData.profileType, state.clients);
      const clientCount = state.clients.length;
      showToast(
        <>
          <span className="font-bold">Relationship {profileName}</span>
          {' '}has been created with{' '}
          <span className="font-bold">{clientCount} client{clientCount === 1 ? '' : 's'}</span>
          . You can now send invitation emails to your client{clientCount === 1 ? '' : 's'}.
        </>
      );
      navigate(`/relationships/${profileId}?tab=clients`);
    }
  };

  const handleClientInputPolicies = () => {
    if (profileId) {
      const profileName = state.profileData.profileName || deriveProfileName(state.profileData.profileType, state.clients);
      const clientCount = state.clients.length;
      showToast(
        <>
          <span className="font-bold">Relationship {profileName}</span>
          {' '}has been created with{' '}
          <span className="font-bold">{clientCount} client{clientCount === 1 ? '' : 's'}</span>
          . You can now send invitation emails to your client{clientCount === 1 ? '' : 's'}.
        </>
      );
      navigate(`/relationships/new/send-invitations?profileId=${profileId}&templateType=add-policies`);
    }
  };

  // Inline edit form rendered inside the list view
  const renderInlineEditForm = (policy: SavedPolicy) => (
    <div className="pt-lg">
      {formErrors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-700">{formErrors.general}</p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-lg">
        {/* Provider */}
        <div>
          <InputLabel required>Provider</InputLabel>
          <Dropdown
            placeholder="Select a provider"
            options={providers.map(p => ({ value: p.providerId, text: p.provider.name }))}
            value={providerId}
            onChange={(val) => {
              setProviderId(val);
              if (formErrors.providerId) setFormErrors(prev => ({ ...prev, providerId: '' }));
            }}
            error={formErrors.providerId}
          />
        </div>

        {/* Policy Category */}
        <div>
          <InputLabel required>Policy type</InputLabel>
          <Dropdown
            placeholder="Select a policy type"
            options={formCategoryOptions}
            value={category}
            onChange={(v) => { setCategory(v); if (formErrors.category) setFormErrors(prev => ({ ...prev, category: '' })); }}
            error={formErrors.category}
          />
        </div>

        {/* Policy Number */}
        <div>
          <InputLabel>Policy number</InputLabel>
          <TextInput
            value={policyNumber}
            onChange={(e) => setPolicyNumber(e.target.value)}
            placeholder="Eg. POL-123456 (optional)"
          />
        </div>
      </div>

      {/* Client owner/signatory list (Joint) — same layout as the add form */}
      {isJoint && state.clients.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between pb-[10px]">
            <span className="text-black font-medium text-body-02 font-sans">Client</span>
            <div className="flex">
              <span className="w-[100px] text-center text-black text-body-02 font-medium font-sans">Owner</span>
              <span className="w-[100px] text-center text-black text-body-02 font-medium font-sans">Signatory</span>
            </div>
          </div>
          {state.clients.map(client => (
            <div key={client.tempId} className="flex items-center justify-between">
              <span className="black font-normal text-body-02 font-sans pb-[12px]">{client.firstName} {client.lastName}</span>
              <div className="flex">
                <div className="w-[100px] flex justify-center">
                  <Checkbox className={POLICY_CHECKBOX_CLASS}
                    checked={clientRoles[client.tempId]?.owner || false}
                    onChange={() => toggleClientRole(client.tempId, 'owner')}
                  />
                </div>
                <div className="w-[100px] flex justify-center">
                  <Checkbox className={POLICY_CHECKBOX_CLASS}
                    checked={clientRoles[client.tempId]?.signatory || false}
                    onChange={() => toggleClientRole(client.tempId, 'signatory')}
                  />
                </div>
              </div>
            </div>
          ))}
          {formErrors.clientRoles && (
            <span className="text-xs text-[#E7000B] mt-1 block">{formErrors.clientRoles}</span>
          )}
        </div>
      )}

      {/* Policy flags — same labels as the add form */}
      {isJoint ? (
        <div className="mt-4 pt-4 border-t border-[#E1E1E1] flex flex-col gap-3">
          <Checkbox className={POLICY_CHECKBOX_CLASS}
            checked={loaInPlace}
            onChange={() => setLoaInPlace(v => !v)}
            label="This policy has a Letter of Authority in place"
          />
          <Checkbox className={POLICY_CHECKBOX_CLASS}
            checked={inAgency}
            onChange={() => setInAgency(v => !v)}
            label="This policy is in agency"
          />
        </div>
      ) : (
        <div className="flex items-center gap-xl mt-md">
          <Checkbox className={POLICY_CHECKBOX_CLASS}
            checked={inAgency}
            onChange={() => setInAgency(v => !v)}
            label="In Agency"
          />
          <Checkbox className={POLICY_CHECKBOX_CLASS}
            checked={loaInPlace}
            onChange={() => setLoaInPlace(v => !v)}
            label="LOA in Place"
          />
        </div>
      )}

      {/* Policy documents — same chip as the add form (already-uploaded + staged) */}
      <div className="mt-6">
        <FileUploadField
          fieldKey="policyDoc"
          files={policyDocs}
          onAddFile={(_k, file) => setPolicyDocs(prev => [...prev, file])}
          onRemoveFile={(_k, index) => setPolicyDocs(prev => prev.filter((_, i) => i !== index))}
        />
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-md mt-6">
        <Button
          type="button"
          variant="outlined"
          colour="red"
          disabled={removing || saving}
          loading={removing}
          onClick={() => handleRemovePolicy(policy.id)}
        >
          Remove Policy
        </Button>
        <Button
          type="button"
          variant="filled"
          disabled={saving || removing || !isFormComplete || (isJoint && !jointOwnerOk)}
          loading={saving}
          onClick={handleSavePolicy}
        >
          Save Details
        </Button>
      </div>
    </div>
  );

  if (loadingConfig) {
    return (
      <div className="flex items-center justify-center gap-[10px] self-stretch mx-lg mt-lg rounded-lg bg-lilac-06" style={{ height: '100px', padding: '40px 16px' }}>
        <div className="w-6 h-6 border-2 border-purple-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
        <span className="text-black font-google text-body-02 font-normal">Loading configuration...</span>
      </div>
    );
  }

  return (
    <div className="pt-lg bg-page-bg flex-1">
      {/* View A: Choice cards */}
      {view === 'choice' && (
        <div className="mx-lg mb-lg">
          <div className='flex flex-col px-md py-lg w-full rounded-card bg-white shadow-[0_0_10px_0_rgba(0,0,0,0.10)]'>
            <div className="font-google text-[#000] text-h5 font-normal leading-normal pb-lg">Add policy details</div>
            <div className='flex flex-row w-full gap-lg'>
              <button
                type="button"
                onClick={() => { initClientRoles(); setView('form'); }}
                className="flex flex-col justify-start bg-lilac-06 rounded-lg flex-1 p-md cursor-pointer border border-transparent hover:border-[#D7D4FF] transition-colors"
              >

                <div className="flex justify-start font-google text-body-01 font-medium leading-normal text-black pb-sm">I want to add the policy details now</div>
                <span className="block text-left font-google text-body-02 font-normal leading-normal text-[#1E1E1E]">If you don't have all the details it's ok, you can save it as a draft and amend it later.</span>
              </button>
              <button
                type="button"
                onClick={handleClientInputPolicies}
                className="flex flex-col justify-start bg-lilac-06 rounded-lg flex-1 p-md cursor-pointer border border-transparent hover:border-[#D7D4FF] transition-colors"
              >

                <div className="flex justify-start font-google text-body-01 font-medium leading-normal text-black pb-sm">I want the client to input the policy details</div>
                <span className="flex justify-start font-google text-body-02 font-normal leading-normal text-[#1E1E1E]">Send an email to the policy holder with a link to input the policy details </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View B: Policy form */}
      {view === 'form' && (
        <>
        <div className="bg-white rounded-card shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)] mx-lg py-lg px-md">
          <div className="font-google text-[#000] text-h5 font-normal leading-normal pb-lg">Add policy details</div>

          {formErrors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-700">{formErrors.general}</p>
            </div>
          )}

          <div className="grid grid-cols-3 gap-lg">
            {/* Provider */}
            <div>
              <InputLabel required>Provider</InputLabel>
              <Dropdown
                placeholder="Select a provider"
                options={providers.map(p => ({ value: p.providerId, text: p.provider.name }))}
                value={providerId}
                onChange={(val) => {
                  setProviderId(val);
                  if (formErrors.providerId) setFormErrors(prev => ({ ...prev, providerId: '' }));
                }}
                error={formErrors.providerId}
              />
            </div>

            {/* Policy Category */}
            <div>
              <InputLabel required>Policy type</InputLabel>
              <Dropdown
                placeholder="Select a policy type"
                options={formCategoryOptions}
                value={category}
                onChange={(v) => { setCategory(v); if (formErrors.category) setFormErrors(prev => ({ ...prev, category: '' })); }}
                error={formErrors.category}
              />
            </div>

            {/* Policy Number */}
            <div>
              <InputLabel>Policy number</InputLabel>
              <TextInput
                value={policyNumber}
                onChange={(e) => setPolicyNumber(e.target.value)}
                placeholder="Eg. POL-123456 (optional)"
              />
            </div>
          </div>

          {/* Client owner/signatory list (Joint) */}
          {isJoint && state.clients.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between pb-[10px]">
                <span className="text-black font-medium text-body-02 font-sans">Client</span>
                <div className="flex">
                  <span className="w-[100px] text-center text-black text-body-02 font-medium font-sans">Owner</span>
                  <span className="w-[100px] text-center text-black text-body-02 font-medium font-sans">Signatory</span>
                </div>
              </div>
              {state.clients.map(client => (
                <div key={client.tempId} className="flex items-center justify-between">
                  <span className="black font-normal text-body-02 font-sans pb-[12px]">{client.firstName} {client.lastName}</span>
                  <div className="flex">
                    <div className="w-[100px] flex justify-center">
                      <Checkbox className={POLICY_CHECKBOX_CLASS}
                        checked={clientRoles[client.tempId]?.owner || false}
                        onChange={() => toggleClientRole(client.tempId, 'owner')}
                      />
                    </div>
                    <div className="w-[100px] flex justify-center">
                      <Checkbox className={POLICY_CHECKBOX_CLASS}
                        checked={clientRoles[client.tempId]?.signatory || false}
                        onChange={() => toggleClientRole(client.tempId, 'signatory')}
                      />
                    </div>
                  </div>
                </div>
              ))}
              {formErrors.clientRoles && (
                <span className="text-xs text-[#E7000B] mt-1 block">{formErrors.clientRoles}</span>
              )}
            </div>
          )}

          {/* Policy flags */}
          {isJoint ? (
            <div className="mt-4 pt-4 border-t border-[#E1E1E1] flex flex-col gap-3">
              <Checkbox className={POLICY_CHECKBOX_CLASS}
                checked={loaInPlace}
                onChange={() => setLoaInPlace(v => !v)}
                label="This policy has a Letter of Authority in place"
              />
              <Checkbox className={POLICY_CHECKBOX_CLASS}
                checked={inAgency}
                onChange={() => setInAgency(v => !v)}
                label="This policy is in agency"
              />
            </div>
          ) : (
            <div className="flex items-center gap-xl mt-md">
              <Checkbox className={POLICY_CHECKBOX_CLASS}
                checked={inAgency}
                onChange={() => setInAgency(v => !v)}
                label="In Agency"
              />
              <Checkbox className={POLICY_CHECKBOX_CLASS}
                checked={loaInPlace}
                onChange={() => setLoaInPlace(v => !v)}
                label="LOA in Place"
              />
            </div>
          )}

          {/* Policy documents */}
          <div className="mt-6">
            <FileUploadField
              fieldKey="policyDoc"
              files={policyDocs}
              onAddFile={(_k, file) => setPolicyDocs(prev => [...prev, file])}
              onRemoveFile={(_k, index) => setPolicyDocs(prev => prev.filter((_, i) => i !== index))}
            />
          </div>

          {/* Save button */}
          <div className="flex justify-end mt-6">
            <Button
              type="button"
              variant="filled"
              disabled={saving || !isFormComplete || (isJoint && !jointOwnerOk)}
              loading={saving}
              onClick={handleSavePolicy}
            >
              Save Details
            </Button>
          </div>
        </div>

        {/* Bottom buttons */}
        <div className="flex justify-between mx-lg mt-lg">
          <div className='flex items-center'>
            <Button type="button" variant="outlined" onClick={() => setView('choice')}>
              Back
            </Button>
          </div>
          <div className='flex items-center gap-md'>
            <Button type="button" variant="outlined" disabled>
              Save Policies
            </Button>
            <Button type="button" variant="filled" disabled>
              Save Policies &amp; Send Invites
            </Button>
          </div>
        </div>
        </>
      )}

      {/* View C: Saved policies list */}
      {view === 'list' && (
        <div className="mx-lg flex flex-col gap-lg">
          <div className="bg-white flex flex-col rounded-card shadow-[0_0_10px_0_rgba(0,0,0,0.10)] overflow-hidden p-lg gap-lg">
            {savedPolicies.map((policy) => {
              const isExpanded = expandedPolicyId === policy.id;
              return (
                <div key={policy.id} className={`w-full flex ${isExpanded ? 'flex-col gap-0' : 'items-center gap-md'} rounded-card border border-[#A3A3A3] bg-white p-lg justify-between`}>
                  <div className="flex w-full items-center justify-between gap-md">
                    <div className="flex flex-row gap-[17.25px] items-center">
                      <AppIcon name="policy success" size={24} />
                      <div className='flex flex-col gap-sm'>
                        <h3 className='font-google text-body-01 font-medium leading-normal text-black !mb-0'>{policy.providerName}
                        {policy.policyTypeName && ` - ${policy.policyTypeName}`}</h3>
                        <span className='font-google text-body-02 font-normal leading-normal text-black'> {policy.ownershipType}
                        {policy.policyNumber && ` | ${policy.policyNumber}`}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-xl">
                      {policy.clientNames.map((name, nameIndex) => (
                        <span
                          key={nameIndex}
                          className="flex items-center justify-center gap-1 px-2 py-1 rounded bg-neutral-bg overflow-hidden text-ellipsis font-google text-sm font-medium text-neutral-text"
                        >
                          {name}
                        </span>
                      ))}
                      <div>
                        <button
                          type="button"
                          onClick={() => handleToggleExpand(policy)}
                          className="flex cursor-pointer items-center justify-center p-1 rounded hover:bg-gray-100 transition-colors"
                          title={isExpanded ? 'Collapse' : 'Edit policy'}
                        >
                          <div
                            className="transition-transform duration-200"
                            style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                          >
                            <AppIcon name="expand chevron" size={24} />
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Inline edit form */}
                  {isExpanded && renderInlineEditForm(policy)}
                </div>
              );
            })}

            {/* Add Another Policy - button (only when there are saved policies) */}
            {!showInlineAdd && savedPolicies.length > 0 && (
              <div className='flex justify-end w-full'>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => { setAddProviderId(''); setAddCategory(''); setAddPolicyNumber(''); setAddFormErrors({}); initAddClientRoles(); setShowInlineAdd(true); }}
                >
                  Add Another Policy
                </Button>
              </div>
            )}

            {/* Inline add form (shown when button clicked OR when no policies exist) */}
            {(showInlineAdd || savedPolicies.length === 0) && (
              <div>
                {addFormErrors.general && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-red-700">{addFormErrors.general}</p>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-lg">
                  {/* Provider */}
                  <div>
                    <InputLabel required>Provider</InputLabel>
                    <Dropdown
                      placeholder="Select a provider"
                      options={providers.map(p => ({ value: p.providerId, text: p.provider.name }))}
                      value={addProviderId}
                      onChange={(val) => {
                        setAddProviderId(val);
                        if (addFormErrors.providerId) setAddFormErrors(prev => ({ ...prev, providerId: '' }));
                      }}
                      error={addFormErrors.providerId}
                    />
                  </div>

                  {/* Policy Category */}
                  <div>
                    <InputLabel required>Policy type</InputLabel>
                    <Dropdown
                      placeholder="Select a policy type"
                      options={addCategoryOptions}
                      value={addCategory}
                      onChange={(v) => { setAddCategory(v); if (addFormErrors.category) setAddFormErrors(prev => ({ ...prev, category: '' })); }}
                      error={addFormErrors.category}
                    />
                  </div>

                  {/* Policy Number */}
                  <div>
                    <InputLabel>Policy number</InputLabel>
                    <TextInput
                      value={addPolicyNumber}
                      onChange={(e) => setAddPolicyNumber(e.target.value)}
                      placeholder="Eg. POL-123456 (optional)"
                    />
                  </div>
                </div>

                {/* Client owner/signatory list (Joint) */}
                {isJoint && state.clients.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between pb-[10px]">
                      <span className="text-black font-medium text-body-02 font-sans">Client</span>
                      <div className="flex">
                        <span className="w-[100px] text-center text-black text-body-02 font-medium font-sans">Owner</span>
                        <span className="w-[100px] text-center text-black text-body-02 font-medium font-sans">Signatory</span>
                      </div>
                    </div>
                    {state.clients.map(client => (
                      <div key={client.tempId} className="flex items-center justify-between">
                        <span className="black font-normal text-body-02 font-sans pb-[12px]">{client.firstName} {client.lastName}</span>
                        <div className="flex">
                          <div className="w-[100px] flex justify-center">
                            <Checkbox className={POLICY_CHECKBOX_CLASS}
                              checked={addClientRoles[client.tempId]?.owner || false}
                              onChange={() => toggleAddClientRole(client.tempId, 'owner')}
                            />
                          </div>
                          <div className="w-[100px] flex justify-center">
                            <Checkbox className={POLICY_CHECKBOX_CLASS}
                              checked={addClientRoles[client.tempId]?.signatory || false}
                              onChange={() => toggleAddClientRole(client.tempId, 'signatory')}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    {addFormErrors.clientRoles && (
                      <span className="text-xs text-[#E7000B] mt-1 block">{addFormErrors.clientRoles}</span>
                    )}
                  </div>
                )}

                {/* Policy flags */}
                {isJoint ? (
                  <div className="mt-4 pt-4 border-t border-[#E1E1E1] flex flex-col gap-3">
                    <Checkbox className={POLICY_CHECKBOX_CLASS}
                      checked={addLoaInPlace}
                      onChange={() => setAddLoaInPlace(v => !v)}
                      label="This policy has a Letter of Authority in place"
                    />
                    <Checkbox className={POLICY_CHECKBOX_CLASS}
                      checked={addInAgency}
                      onChange={() => setAddInAgency(v => !v)}
                      label="This policy is in agency"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-xl mt-md">
                    <Checkbox className={POLICY_CHECKBOX_CLASS}
                      checked={addInAgency}
                      onChange={() => setAddInAgency(v => !v)}
                      label="In Agency"
                    />
                    <Checkbox className={POLICY_CHECKBOX_CLASS}
                      checked={addLoaInPlace}
                      onChange={() => setAddLoaInPlace(v => !v)}
                      label="LOA in Place"
                    />
                  </div>
                )}

                {/* Policy documents */}
                <div className="mt-6">
                  <FileUploadField
                    fieldKey="addPolicyDoc"
                    files={addPolicyDocs}
                    onAddFile={(_k, file) => setAddPolicyDocs(prev => [...prev, file])}
                    onRemoveFile={(_k, index) => setAddPolicyDocs(prev => prev.filter((_, i) => i !== index))}
                  />
                </div>

                {/* Action buttons */}
                <div className="flex justify-end gap-md mt-6">
                  {savedPolicies.length > 0 && (
                    <Button
                      type="button"
                      variant="outlined"
                      disabled={saving}
                      onClick={() => { setShowInlineAdd(false); setAddProviderId(''); setAddCategory(''); setAddPolicyNumber(''); setAddClientRoles({}); setAddFormErrors({}); }}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="filled"
                    disabled={saving || !isAddFormComplete || (isJoint && !addJointOwnerOk)}
                    loading={saving}
                    onClick={handleSaveAddPolicy}
                  >
                    Save Details
                  </Button>
                </div>
              </div>
            )}
          </div>


          {/* Bottom buttons - active */}
          <div className="flex justify-between pb-lg">
            <div className='flex items-center'>
              <Button type="button" variant="outlined" onClick={() => setView('choice')}>
                Back
              </Button>
            </div>
            <div className='flex items-center gap-md'>
              <Button type="button" variant="outlined" onClick={handleDone}>
                Save Policies
              </Button>
              <Button
                type="button"
                variant="filled"
                onClick={() => {
                  if (profileId) {
                    navigate(`/relationships/new/send-invitations?profileId=${profileId}`);
                  }
                }}
              >
                Save Policies &amp; Send Invites
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
